import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, colones, monthRange, toLocalISO } from "@/lib/api";
import { computePayrollForEmployee, aplicaJornada48, cuentasSalario, ACTIVIDAD_JORNADAS_ESPECIALES } from "@/lib/payroll";
import { aplicaRenta, rentaPeriodo, aplicarRentaACalc } from "@/lib/renta";
import HorasJornadaCell from "@/components/HorasJornadaCell";
import { CalendarRange, ChevronLeft, ChevronRight, FileSpreadsheet } from "lucide-react";
import ConceptoCell, { AusenteCell } from "@/components/ConceptoCell";
import { buildPuestosMap, puestoNoHorasExtras } from "@/lib/puestos";
import { generarReportePlanilla } from "@/lib/reporteExcel";
import { subPeriodsForMonth, buildOverrideFromDoc } from "@/lib/subperiods";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

export default function PlanillasMensuales() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [empleados, setEmpleados] = useState([]);
  const [asistencia, setAsistencia] = useState([]);
  const [rebajos, setRebajos] = useState([]);
  const [bonificaciones, setBonificaciones] = useState([]);
  const [overrides, setOverrides] = useState([]); // lista cruda de overrides
  const [empresa, setEmpresa] = useState({});

  const range = useMemo(() => monthRange(year, month), [year, month]);

  useEffect(() => {
    (async () => {
      const [e, a, r, b, emp, ovr] = await Promise.all([
        api.get("/empleados"),
        api.get(`/asistencia?fecha_inicio=${range.start}&fecha_fin=${range.end}`),
        api.get("/rebajos"),
        api.get("/bonificaciones"),
        api.get("/empresa"),
        api.get("/planilla-overrides"),
      ]);
      setEmpleados(e.data); setAsistencia(a.data);
      setRebajos(r.data); setBonificaciones(b.data);
      setEmpresa(emp.data || {});
      setOverrides(ovr.data || []);
    })();
    // range.start/end are the only reactive deps; api and state setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  const calculations = useMemo(() => {
    const puestosMap = buildPuestosMap(empresa);
    const grupoAct = empresa?.actividad_grupo;
    const ccssPensPct = Number(empresa?.ccss_pensionado_pct);
    const ccssPensRate = (isFinite(ccssPensPct) && ccssPensPct > 0) ? ccssPensPct / 100 : null;
    return empleados.filter(e => {
      // Sólo mostrar empleados cuya fecha_ingreso sea <= fin del período mensual.
      if (e.fecha_ingreso && String(e.fecha_ingreso).slice(0, 10) > range.end) return false;
      // Regla: si está activo → incluir. Si está inactivo (despedido/renuncia) →
      // incluir SOLO si su fecha_salida cae dentro o después del mes (trabajó
      // parte del mes). Esto es necesario para el trámite de exclusión CCSS.
      if (e.estado === "Activo") return true;
      if (e.estado === "Inactivo") {
        const fs = e.fecha_salida ? String(e.fecha_salida).slice(0, 10) : "";
        if (fs && fs >= range.start) return true;
      }
      return false;
    }).map(emp => {
      const ast = asistencia.filter(a => a.empleado_id === emp.id);
      const rebs = rebajos.filter(r => {
        if (r.empleado_id !== emp.id || !r.activo) return false;
        const f = r.fecha || r.fecha_inicio;
        return f && f >= range.start && f <= range.end;
      });
      const bons = bonificaciones.filter(b => {
        if (b.empleado_id !== emp.id || !b.activo) return false;
        return b.fecha && b.fecha >= range.start && b.fecha <= range.end;
      });
      const noHorasExtras = puestoNoHorasExtras(puestosMap, emp.puesto);
      const freq = String(emp.frecuencia_pago || "Mensual").toLowerCase();
      const diasBase = (grupoAct === "servicios_industria" && freq === "semanal") ? 26 : 30;
      const ccssRate = ccssPensRate;

      // Sub-períodos del mes según frecuencia (semanal / quincenal / mensual)
      const periods = subPeriodsForMonth(year, month, freq);

      // Estructura para acumular resultados
      const zeroCounts = { Diurna: 0, Mixta: 0, Nocturna: 0 };
      const zeroJornada = () => ({ Diurna: { hrs: 0, monto: 0 }, Mixta: { hrs: 0, monto: 0 }, Nocturna: { hrs: 0, monto: 0 } });
      const zeroBreakdown = () => ({
        diaLibre: { dias: 0, monto: 0 },
        feriado: { dias: 0, monto: 0 },
        incapacidadCCSS: { dias: 0, monto: 0 },
        incapacidadINS: { dias: 0, monto: 0 },
        vacaciones: { dias: 0, monto: 0 },
        licencia: { dias: 0, monto: 0 },
        bonificaciones: { dias: 0, monto: 0 },
      });
      const acc = {
        montoOrd: 0, montoExt: 0, montoFer: 0, totalBruto: 0,
        totalRebajos: 0, ccss: 0, renta: 0, neto: 0, montoBon: 0,
        horasOrd: { ...zeroCounts }, horasExt: { ...zeroCounts },
        horasFeriado: 0, diasLibres: 0, feriadosCount: 0,
        salarioTotal: 0, salarioProrateado: 0,
        diasTrab: 0, hrsOrd: 0, hrsExt: 0,
        byJornadaOrd: zeroJornada(),
        byJornadaExt: zeroJornada(),
        breakdown: zeroBreakdown(),
      };
      // Guardamos el último `calc` para conservar campos no numéricos (unidad, tipo pago, etc.)
      let lastCalc = null;

      let brutoPrimera = 0;
      for (const p of periods) {
        // Filtrar los datos de este sub-período
        const astP = ast.filter(a => a.fecha >= p.start && a.fecha <= p.end);
        const rebsP = rebs.filter(r => {
          const f = r.fecha || r.fecha_inicio;
          return f && f >= p.start && f <= p.end;
        });
        const bonsP = bons.filter(b => b.fecha && b.fecha >= p.start && b.fecha <= p.end);
        // Override manual para este sub-período. Se aplica SIEMPRE que exista
        // en la base (ya sea planilla editable regular o histórico retroactivo).
        const o = overrides.find(x => x.empleado_id === emp.id && x.fecha_inicio === p.start && x.fecha_fin === p.end);
        const override = buildOverrideFromDoc(o);
        const calc = computePayrollForEmployee(emp, astP, {
          rebajos: rebsP, bonificaciones: bonsP,
          extrasDoblesFeriado: !!empresa.extras_dobles_feriado,
          ccssAplicaExtras: !!empresa.ccss_aplica_extras,
          noHorasExtras, diasBase, ccssRate, override,
          periodStart: p.start, periodEnd: p.end,
          jornada48: aplicaJornada48(empresa, emp),
        });
        if (aplicaRenta(empresa, emp)) {
          aplicarRentaACalc(calc, rentaPeriodo({ empresa, emp, calc, periodStart: p.start, periodEnd: p.end, brutoPrimera }));
          acc.renta += calc.renta;
        }
        brutoPrimera = calc.totalBruto || 0;
        lastCalc = calc;
        acc.montoOrd += calc.montoOrd || 0;
        acc.montoExt += calc.montoExt || 0;
        acc.montoFer += calc.montoFer || 0;
        acc.totalBruto += calc.totalBruto || 0;
        acc.totalRebajos += calc.totalRebajos || 0;
        acc.ccss += calc.ccss || 0;
        acc.neto += calc.neto || 0;
        acc.montoBon += calc.montoBon || 0;
        acc.horasFeriado += calc.horasFeriado || 0;
        acc.diasLibres += calc.diasLibres || 0;
        acc.feriadosCount += calc.feriadosCount || 0;
        // Días laborados y totales de horas: acumulan por sub-período. El
        // valor `calc.diasTrab` viene de la asistencia del sub-período, así
        // que la suma equivale al total del mes.
        acc.diasTrab += calc.diasTrab || 0;
        acc.hrsOrd += calc.hrsOrd || 0;
        acc.hrsExt += calc.hrsExt || 0;
        for (const j of ["Diurna", "Mixta", "Nocturna"]) {
          acc.horasOrd[j] += (calc.horasOrd?.[j] || 0);
          acc.horasExt[j] += (calc.horasExt?.[j] || 0);
          if (calc.byJornadaOrd?.[j]) {
            acc.byJornadaOrd[j].hrs += calc.byJornadaOrd[j].hrs || 0;
            acc.byJornadaOrd[j].monto += calc.byJornadaOrd[j].monto || 0;
          }
          if (calc.byJornadaExt?.[j]) {
            acc.byJornadaExt[j].hrs += calc.byJornadaExt[j].hrs || 0;
            acc.byJornadaExt[j].monto += calc.byJornadaExt[j].monto || 0;
          }
        }
        if (calc.breakdown) {
          for (const key of Object.keys(acc.breakdown)) {
            if (calc.breakdown[key]) {
              acc.breakdown[key].dias += calc.breakdown[key].dias || 0;
              acc.breakdown[key].monto += calc.breakdown[key].monto || 0;
            }
          }
        }
      }

      // Merge del último calc (para conservar `unidad`, tarifas, etc.) con los totales sumados
      const merged = { ...(lastCalc || {}), ...acc };
      return { emp, ast, rebs, bons, calc: merged, noHorasExtras, diasBase };
    });
  }, [empleados, asistencia, rebajos, bonificaciones, overrides, year, month, range, empresa]);

  const showRenta = empresa?.actividad_grupo === ACTIVIDAD_JORNADAS_ESPECIALES;
  const totals = useMemo(() => calculations.reduce((acc, c) => ({
    bruto: acc.bruto + c.calc.totalBruto,
    extras: acc.extras + c.calc.montoExt,
    rebajos: acc.rebajos + c.calc.totalRebajos,
    ccss: acc.ccss + c.calc.ccss,
    renta: acc.renta + (c.calc.renta || 0),
    neto: acc.neto + c.calc.neto,
    aguinaldo: acc.aguinaldo + (c.calc.totalBruto / 12),
  }), { bruto: 0, extras: 0, rebajos: 0, ccss: 0, renta: 0, neto: 0, aguinaldo: 0 }), [calculations]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const exportarExcel = () => {
    const periodoLabel = `${MONTHS_ES[month]} ${year}`;
    generarReportePlanilla({
      kind: "mensual",
      empresa,
      calculations,
      totals,
      periodoLabel,
      fileNameLabel: `Planilla_${MONTHS_ES[month]}_${year}`,
      sheetNameLabel: `${MONTHS_ES[month].slice(0,3)} ${year}`,
    });
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><CalendarRange size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Planillas Mensuales</h1>
            <p className="text-sm text-secondary-c">{MONTHS_ES[month]} {year}</p>
          </div>
        </div>
        <button
          onClick={exportarExcel}
          disabled={calculations.length === 0}
          className="np-btn np-btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="export-excel-btn"
        >
          <FileSpreadsheet size={16} /> Reporte
        </button>
      </header>

      <div className="np-card p-4 mb-5 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="np-btn np-btn-outline p-2"><ChevronLeft size={16} /></button>
          <div className="px-4 py-2 bg-[#FAFAF7] rounded-xl text-sm font-medium font-display" data-testid="month-label">{MONTHS_ES[month]} {year}</div>
          <button onClick={nextMonth} className="np-btn np-btn-outline p-2"><ChevronRight size={16} /></button>
        </div>
        <div className="flex gap-4 text-sm flex-wrap">
          <div><span className="text-secondary-c">Total bruto:</span> <span className="font-semibold text-primary-c">{colones(totals.bruto)}</span></div>
          <div><span className="text-secondary-c">Extras:</span> <span className="font-semibold text-warning">{colones(totals.extras)}</span></div>
          <div><span className="text-secondary-c">Rebajos:</span> <span className="font-semibold text-error">{colones(totals.rebajos + totals.ccss + totals.renta)}</span></div>
          <div><span className="text-secondary-c">Neto:</span> <span className="font-semibold text-success" data-testid="mensual-neto">{colones(totals.neto)}</span></div>
          <div title="Provisión de aguinaldo del mes = Bruto ÷ 12 (Art. 12 CT)"><span className="text-secondary-c">Aguinaldo (prov.):</span> <span className="font-semibold text-primary-c" data-testid="mensual-aguinaldo-prov">{colones(totals.aguinaldo)}</span></div>
        </div>
      </div>

      <div className="np-card p-0 np-scroll">
        <table className="np-table np-table-compact">
          <thead><tr>
            <th>Empleado</th>
            <th>Identificación</th>
            <th>Hrs Ord</th>
            <th>Hrs Ext</th>
            <th>Día Libre</th>
            <th>Feriado</th>
            <th>Incapacidad</th>
            <th>Vacaciones</th>
            <th>Licencia</th>
            <th>Ausente</th>
            <th>Bonificaciones</th>
            <th>Monto Ext</th>
            <th>Bruto</th>
            <th>CCSS</th>
            {showRenta && <th>Imp. Renta</th>}
            <th>Rebajos</th>
            <th>Neto</th>
            <th title="Provisión de aguinaldo del mes: Bruto ÷ 12">Aguinaldo (prov.)</th>
          </tr></thead>
          <tbody>
            {calculations.map(({ emp, calc }) => {
              const b = calc.breakdown || {};
              const incSub = b.incapacidadCCSS && b.incapacidadCCSS.diasNoPagos ? `${b.incapacidadCCSS.diasNoPagos} no pago(s)` : null;
              return (
              <tr key={emp.id}>
                <td><div className="font-medium">{emp.nombre} {emp.apellidos}</div><div className="text-xs text-secondary-c">{emp.puesto} · {emp.departamento}</div>{cuentasSalario(emp, range.end) && <span className="np-badge np-badge-neutral mt-1 inline-block text-[10px]" data-testid={`mensual-2cuentas-${emp.cedula}`}>2 cuentas</span>}</td>
                <td className="text-xs whitespace-nowrap" data-testid={`mensual-cedula-${emp.cedula}`}>{emp.cedula || "—"}</td>
                <td><HorasJornadaCell byJornada={calc.byJornadaOrd} showMonto /></td>
                <td><HorasJornadaCell byJornada={calc.byJornadaExt} showMonto montoColor="text-warning" /></td>
                <td><ConceptoCell dias={b.diaLibre?.dias} monto={b.diaLibre?.monto} /></td>
                <td><ConceptoCell dias={b.feriado?.dias} monto={b.feriado?.monto} /></td>
                <td><ConceptoCell dias={b.incapacidadCCSS?.dias} monto={b.incapacidadCCSS?.monto} sub={incSub} /></td>
                <td><ConceptoCell dias={b.vacaciones?.dias} monto={b.vacaciones?.monto} /></td>
                <td><ConceptoCell dias={b.licencia?.dias} monto={b.licencia?.monto} /></td>
                <td><AusenteCell dias={b.ausente?.dias} testId={`mensual-ausente-${emp.cedula}`} /></td>
                <td>
                  {b.bonificaciones?.cantidad ? (
                    <div>
                      <div className="font-medium text-xs">{b.bonificaciones.cantidad} {b.bonificaciones.cantidad === 1 ? "bono" : "bonos"}</div>
                      <div className="text-[11px] text-success font-medium">+{colones(b.bonificaciones.monto)}</div>
                      {b.bonificaciones.montoCCSS > 0 && <div className="text-[10px] text-error">CCSS: {colones(b.bonificaciones.montoCCSS)}</div>}
                    </div>
                  ) : <span className="text-secondary-c">—</span>}
                </td>
                <td className={calc.montoExt > 0 ? "text-warning font-medium" : ""}>{calc.montoExt ? colones(calc.montoExt) : "—"}</td>
                <td className="font-medium">{colones(calc.totalBruto)}</td>
                <td className="text-error">{calc.ccss ? "−" + colones(calc.ccss) : "—"}</td>
                {showRenta && <td className="text-error" data-testid={`mensual-renta-${emp.cedula}`}>{calc.renta ? "−" + colones(calc.renta) : "—"}</td>}
                <td className="text-error">{calc.totalRebajos ? "−" + colones(calc.totalRebajos) : "—"}</td>
                <td className="font-semibold text-success">{colones(calc.neto)}</td>
                <td className="text-primary-c font-medium" data-testid={`mensual-aguinaldo-${emp.cedula}`}>{colones(calc.totalBruto / 12)}</td>
              </tr>
            );})}
            <tr className="bg-[#FAFAF7] font-semibold">
              <td>Total General</td><td colSpan={10}></td>
              <td className="text-warning">{colones(totals.extras)}</td>
              <td>{colones(totals.bruto)}</td>
              <td className="text-error">−{colones(totals.ccss)}</td>
              {showRenta && <td className="text-error" data-testid="mensual-total-renta">−{colones(totals.renta)}</td>}
              <td className="text-error">−{colones(totals.rebajos)}</td>
              <td className="text-success">{colones(totals.neto)}</td>
              <td className="text-primary-c">{colones(totals.aguinaldo)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
